import { Thread } from './thread';
import { UIBlock } from './uiblock';

/**
 * ThreadManager manages all threads globally
 * Provides methods to create, retrieve, and delete threads.
 * Includes automatic cleanup to prevent unbounded memory growth.
 */
export class ThreadManager {
  private static instance: ThreadManager;
  private threads: Map<string, Thread>;
  private cleanupInterval: NodeJS.Timeout | null = null;

  // Threads older than 7 days are cleaned up
  private readonly threadTtlMs = 7 * 24 * 60 * 60 * 1000;

  private constructor() {
    this.threads = new Map();
    this.startCleanup();
  }

  /**
   * Periodically remove threads older than 7 days.
   * Runs every hour to avoid frequent iteration over the map.
   */
  private startCleanup(): void {
    this.cleanupInterval = setInterval(() => {
      const now = Date.now();
      let removedCount = 0;
      for (const [id, thread] of this.threads.entries()) {
        if (now - thread.getCreatedAt().getTime() > this.threadTtlMs) {
          this.threads.delete(id);
          removedCount++;
        }
      }
      if (removedCount > 0) {
        console.log(`[ThreadManager] Cleaned up ${removedCount} threads older than 7 days (${this.threads.size} remaining)`);
      }
    }, 24 * 60 * 60 * 1000); // Run once per day
  }

  /**
   * Get singleton instance of ThreadManager
   */
  static getInstance(): ThreadManager {
    if (!ThreadManager.instance) {
      ThreadManager.instance = new ThreadManager();
    }
    return ThreadManager.instance;
  }

  /**
   * Create a new thread
   * @param id - Optional custom ID, generates UUID if not provided
   * @returns The created Thread instance
   */
  createThread(id?: string): Thread {
    const thread = new Thread(id);
    this.threads.set(thread.getId(), thread);
    return thread;
  }

  /**
   * Get a thread by ID
   */
  getThread(id: string): Thread | undefined {
    return this.threads.get(id);
  }

  /**
   * Get all threads
   */
  getAllThreads(): Thread[] {
    return Array.from(this.threads.values());
  }

  /**
   * Get threads as a Map
   */
  getThreadsMap(): Map<string, Thread> {
    return new Map(this.threads);
  }

  /**
   * Delete a thread by ID
   */
  deleteThread(id: string): boolean {
    return this.threads.delete(id);
  }

  /**
   * Check if thread exists
   */
  hasThread(id: string): boolean {
    return this.threads.has(id);
  }

  /**
   * Get number of threads
   */
  getThreadCount(): number {
    return this.threads.size;
  }

  /**
   * Clear all threads
   */
  clearAll(): void {
    this.threads.clear();
  }

  /**
   * Find a UIBlock by ID across all threads
   * @param uiBlockId - The UIBlock ID to search for
   * @returns Object with thread and uiBlock if found, undefined otherwise
   */
  findUIBlockById(uiBlockId: string): { thread: Thread; uiBlock: UIBlock } | undefined {
    for (const thread of this.threads.values()) {
      const uiBlock = thread.getUIBlock(uiBlockId);
      if (uiBlock) {
        return { thread, uiBlock };
      }
    }
    return undefined;
  }

  /**
   * Convert all threads to JSON-serializable object
   */
  toJSON(): Record<string, any> {
    return {
      threads: Array.from(this.threads.values()).map(thread => thread.toJSON()),
      count: this.threads.size,
    };
  }
}
